import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PvForecastingConfig,
  PvForecastingConfigCreate,
  PvForecastingConfigUpdate,
} from '@core/models';
import { PvForecastingService } from '@core/services';
import {
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PvForecastFormComponent } from '../pv-forecast-form/pv-forecast-form.component';
import {
  PvForecastEmeterChannel,
  PvForecastFormGroup,
  createPvForecastForm,
  pvForecastFormValue,
} from '../pv-forecast-form/pv-forecast-form';

export interface PvForecastDialogData {
  mode: 'create' | 'edit';
  deviceId: number;
  emeterChannels: PvForecastEmeterChannel[];
  config?: PvForecastingConfig;
}

@Component({
  selector: 'xemex-pv-forecast-dialog',
  imports: [
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    PvForecastFormComponent,
  ],
  templateUrl: './pv-forecast-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PvForecastDialogComponent {
  readonly dialogRef = inject(MatDialogRef<PvForecastDialogComponent>);
  private readonly pvForecastingService = inject(PvForecastingService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly data = inject<PvForecastDialogData>(MAT_DIALOG_DATA);
  readonly isEdit = this.data.mode === 'edit';
  readonly isSaving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly form: PvForecastFormGroup = createPvForecastForm(
    this.isEdit ? this.data.config : null,
    this.data.emeterChannels,
  );

  onSave(): void {
    const value = pvForecastFormValue(this.form);
    if (!value) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage.set(null);
    this.isSaving.set(true);

    const request$ = this.isEdit
      ? this.pvForecastingService.updateConfig(this.data.deviceId, {
          ...value,
          active: this.data.config?.active ?? true,
        } satisfies PvForecastingConfigUpdate)
      : this.pvForecastingService.createConfig({
          ...value,
          device_id: this.data.deviceId,
          active: true,
        } satisfies PvForecastingConfigCreate);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (config: PvForecastingConfig) => this.dialogRef.close(config),
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.errorMessage.set(this.toErrorMessage(error));
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Validation errors (HTTP 422) describe what the user must fix, so they are
   * shown verbatim; anything else is an unexpected failure shown as a generic message.
   */
  private toErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 422) {
      const detail = error.error?.detail;
      if (Array.isArray(detail)) {
        const messages = detail
          .map((item: { msg?: string }) => item?.msg)
          .filter((msg): msg is string => !!msg);
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
    }
    return this.translate.instant('devices.pvForecast.genericError');
  }
}
